/**
 * @module components/MatchMeter
 * @description Live progress panels. One panel per edition, three
 * presentations each (hero banner, ballroom screen, compact):
 * - Gala: "$X of $100,000 unlocked" 1:1 matching-gift meter.
 * - Campaign: "Our Love Challenge" — friends who have given and the real
 *   total given, with no matching calculation (Samantha, 2026-10-01).
 * Both read pledges through the data hooks, so every open tab updates the
 * moment a pledge is recorded.
 */
import { useEffect, useRef, useState } from "react";
import { CAMPAIGN, formatUsd, friendsHaveGiven } from "../config/campaign";
import { useOfflineCount, useOfflineTotal, usePledges } from "../data";
import { computeGivingProgress, computeMatchProgress } from "../domain/match";
import { useEdition } from "../app/edition";
import { HummingbirdGlyph } from "./Brand";

export type MatchMeterVariant = "hero" | "screen" | "compact";

/** Hummingbird marker, shared by the meter track and the Love Challenge mark. */
function Hummingbird({ width = 40, height = 24 }: { width?: number; height?: number }) {
    return (
        <svg viewBox="0 0 40 24" width={width} height={height} aria-hidden="true">
            <HummingbirdGlyph />
        </svg>
    );
}

/**
 * Eases a number toward its target so totals "count up" on change.
 * Honors prefers-reduced-motion by jumping straight to the value.
 */
function useCountUp(target: number, durationMs = 1200): number {
    const [value, setValue] = useState(target);
    const from = useRef(target);
    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setValue(target);
            return;
        }
        const start = performance.now();
        const origin = from.current;
        let frame = 0;
        const tick = (now: number) => {
            const t = Math.min((now - start) / durationMs, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(origin + (target - origin) * eased));
            if (t < 1) frame = requestAnimationFrame(tick);
            else from.current = target;
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, durationMs]);
    return value;
}

/** The progress panel for the active edition. Pages use this, never a specific panel. */
export function ChallengePanel({ variant = "hero" }: { variant?: MatchMeterVariant }) {
    return useEdition().showMatch ? <MatchMeter variant={variant} /> : <LoveChallenge variant={variant} />;
}

/** Campaign edition: friends who have given, and the actual amount given. */
export function LoveChallenge({ variant = "hero" }: { variant?: MatchMeterVariant }) {
    const progress = computeGivingProgress(usePledges(), useOfflineTotal(), useOfflineCount());
    const friends = useCountUp(progress.donorCount, 900);
    const given = useCountUp(progress.raised);
    return (
        <section className={`meter meter--${variant} meter--love`} aria-label="Our Love Challenge progress">
            <p className="meter-kicker">Our Love Challenge</p>
            <p className="meter-figure">
                <span className="meter-unlocked">{friends}</span>
                <span className="meter-of"> {friendsHaveGiven(progress.donorCount)}</span>
            </p>
            <div className="meter-love-mark" aria-hidden="true">
                <span className="meter-love-rule" />
                <Hummingbird />
                <span className="meter-love-rule" />
            </div>
            <p className="meter-meta">
                Total impact <strong>{formatUsd(given)}</strong> given for Jamaica
            </p>
        </section>
    );
}

/** Gala edition: the 1:1 matching-gift meter. */
export function MatchMeter({ variant = "hero" }: { variant?: MatchMeterVariant }) {
    const progress = computeMatchProgress(usePledges(), CAMPAIGN.matchCap, useOfflineTotal(), useOfflineCount());
    const unlocked = useCountUp(progress.unlocked);
    const impact = useCountUp(progress.totalImpact);
    const pct = Math.round(progress.fraction * 1000) / 10;
    const complete = progress.remaining === 0;

    return (
        <section className={`meter meter--${variant}`} aria-label="Matching gift challenge progress">
            <p className="meter-kicker">{CAMPAIGN.matchRatioLabel} Matching Gift Challenge</p>
            <p className="meter-figure">
                <span className="meter-unlocked">{formatUsd(unlocked)}</span>
                <span className="meter-of"> of {formatUsd(CAMPAIGN.matchCap)} unlocked</span>
            </p>
            <div
                className="meter-track"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={CAMPAIGN.matchCap}
                aria-valuenow={progress.unlocked}
                aria-valuetext={`${formatUsd(progress.unlocked)} of ${formatUsd(CAMPAIGN.matchCap)} unlocked`}
            >
                <div className="meter-fill" style={{ width: `${pct}%` }} />
                <span className="meter-bird" style={{ left: `clamp(20px, ${pct}%, calc(100% - 20px))` }} aria-hidden="true">
                    <Hummingbird />
                </span>
            </div>
            <p className="meter-meta">
                {complete ? (
                    <strong>Challenge met — thank you! Every gift still changes lives.</strong>
                ) : (
                    <>
                        <strong>{formatUsd(progress.remaining)}</strong> left to unlock ·{" "}
                        <strong>{progress.donorCount}</strong> {friendsHaveGiven(progress.donorCount)} ·
                        total impact <strong>{formatUsd(impact)}</strong>
                    </>
                )}
            </p>
        </section>
    );
}
